/**
 * Program Editor Redux Slice
 * Feature: 028-we-want-to - Program Builder Admin Page
 *
 * Manages state for the two-stage program creation/editing workflow:
 * - Stage 1: Initial program details form
 * - Stage 2: Tabbed builder with Details, Content, Permissions
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store";
import type { SubscriptionAvailability } from "@/api/bundles/types";
import type { EnhancementBundle } from "@/api/enhancementBundles/types";
import {
  createEnhancementBundle,
  fetchEnhancementBundle,
  updateEnhancementBundle,
} from "@/api/enhancementBundles";
import type {
  Program,
  ProgramComponents,
  ProgramEditorTab,
  ProgramDetailsFormValues,
  ProgramAccessStatus,
  SaveStatus,
} from "../types";
import { getDefaultSubscriptionAvailability } from "../types";

// ============================================================================
// State Interface
// ============================================================================

export interface ProgramEditorState {
  // Workflow state
  createdProgramId: string | null; // null = Stage 1, set = Stage 2
  activeTab: ProgramEditorTab;

  // Program data
  programDetails: ProgramDetailsFormValues | null;
  components: ProgramComponents;
  subscriptionAvailability: SubscriptionAvailability;

  // Auto-save state
  saveStatus: SaveStatus;
  lastSavedAt: string | null;
  pendingChanges: boolean;

  // UI state
  isCoursesPickerOpen: boolean;
  isEventsPickerOpen: boolean;

  // Loading states (per operation)
  loading: {
    createProgram: boolean;
    loadProgram: boolean;
    updateProgram: boolean;
  };

  // Error states (per operation)
  error: {
    createProgram: string | null;
    loadProgram: string | null;
    updateProgram: string | null;
  };
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: ProgramEditorState = {
  createdProgramId: null,
  activeTab: "details",
  programDetails: null,
  components: {
    courses: [],
    events: [],
    resourceTags: [],
  },
  subscriptionAvailability: getDefaultSubscriptionAvailability(),
  saveStatus: "idle",
  lastSavedAt: null,
  pendingChanges: false,
  isCoursesPickerOpen: false,
  isEventsPickerOpen: false,
  loading: {
    createProgram: false,
    loadProgram: false,
    updateProgram: false,
  },
  error: {
    createProgram: null,
    loadProgram: null,
    updateProgram: null,
  },
};

// ============================================================================
// Mapping Functions
// ============================================================================

/**
 * Map API EnhancementBundle to frontend Program type
 */
function mapBundleToProgram(bundle: EnhancementBundle): Program {
  return {
    id: bundle.id!,
    name: bundle.name,
    description:
      (bundle as unknown as { description?: string }).description ?? null,
    tagline: (bundle as unknown as { tagline?: string }).tagline ?? null,
    thumbnailUrl:
      (bundle as unknown as { thumbnailUrl?: string }).thumbnailUrl ?? null,
    isPublished:
      (bundle as unknown as { isPublished?: boolean }).isPublished ?? false,
    price: (bundle as unknown as { price?: number }).price ?? null,
    subscriptionAvailability:
      (
        bundle as unknown as {
          subscriptionAvailability?: SubscriptionAvailability;
        }
      ).subscriptionAvailability ?? {},
    components: {
      courses:
        (bundle as unknown as { components?: { courses?: string[] } })
          .components?.courses ?? [],
      events:
        (bundle as unknown as { components?: { events?: string[] } }).components
          ?.events ?? [],
      resourceTags: bundle.resource_tags ?? [],
    },
    createdAt: bundle.createdAt,
    updatedAt: bundle.updatedAt,
  };
}

/**
 * Map frontend ProgramDetailsFormValues to API create payload
 */
function mapDetailsToCreatePayload(
  details: ProgramDetailsFormValues,
): EnhancementBundle {
  return {
    name: details.name,
    components: {
      pre_work: { enabled: false, required: false },
      reinforcement: { enabled: false, duration_weeks: 0 },
      cohort_access: { enabled: false, private_channel: false },
      certification: { enabled: false },
    },
    pricing_rules: {
      base_fee: details.price ?? 0,
      per_seat_multiplier: 1,
      delivery_type_modifiers: { virtual: 1, in_person: 1, hybrid: 1 },
      volume_discounts: [],
    },
    timing_config: {
      pre_work_window_days_before_event: 7,
      reinforcement_start_days_after_event: 1,
      cohort_access_duration_days: 30,
    },
    cohort_size_min: 1,
    cohort_size_max: 100,
    resource_tags: [],
  } as EnhancementBundle;
}

/**
 * Map frontend Program updates to API update payload
 */
function mapProgramToUpdatePayload(
  updates: Partial<Program>,
): Partial<EnhancementBundle> {
  const payload: Partial<EnhancementBundle> & Record<string, unknown> = {};

  if (updates.name !== undefined) payload.name = updates.name;
  if (updates.description !== undefined)
    payload.description = updates.description;
  if (updates.tagline !== undefined) payload.tagline = updates.tagline;
  if (updates.thumbnailUrl !== undefined)
    payload.thumbnailUrl = updates.thumbnailUrl;
  if (updates.isPublished !== undefined)
    payload.isPublished = updates.isPublished;
  if (updates.price !== undefined) {
    payload.pricing_rules = {
      base_fee: updates.price ?? 0,
      per_seat_multiplier: 1,
      delivery_type_modifiers: { virtual: 1, in_person: 1, hybrid: 1 },
      volume_discounts: [],
    };
  }
  if (updates.subscriptionAvailability !== undefined) {
    payload.subscriptionAvailability = updates.subscriptionAvailability;
  }
  if (updates.components !== undefined) {
    payload.components = {
      pre_work: { enabled: false, required: false },
      reinforcement: { enabled: false, duration_weeks: 0 },
      cohort_access: { enabled: false, private_channel: false },
      certification: { enabled: false },
    };
    payload.resource_tags = updates.components.resourceTags;
    payload.registration_settings = {
      required: false,
      event_ids: updates.components.events,
    };
    // Note: courses are stored in a separate field or need API extension
    // For now, we store course IDs as a custom field that the API may need to handle
    (payload as Record<string, unknown>).course_ids =
      updates.components.courses;
  }

  return payload as Partial<EnhancementBundle>;
}

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Load program for editing
 */
export const loadProgramForEdit = createAsyncThunk<
  Program,
  string,
  { rejectValue: string }
>(
  "programEditor/loadProgramForEdit",
  async (programId, { rejectWithValue }) => {
    try {
      const response = await fetchEnhancementBundle(programId);
      return mapBundleToProgram(response);
    } catch {
      return rejectWithValue("Failed to load program");
    }
  },
);

/**
 * Create new program (Stage 1 -> Stage 2 transition)
 */
export const createProgram = createAsyncThunk<
  Program,
  ProgramDetailsFormValues,
  { rejectValue: string }
>("programEditor/createProgram", async (details, { rejectWithValue }) => {
  try {
    const payload = mapDetailsToCreatePayload(details);
    const response = await createEnhancementBundle(payload);
    return mapBundleToProgram(response);
  } catch {
    return rejectWithValue("Failed to create program");
  }
});

/**
 * Update program (auto-save in Stage 2)
 */
export const updateProgram = createAsyncThunk<
  Program,
  { programId: string; updates: Partial<Program> },
  { rejectValue: string }
>(
  "programEditor/updateProgram",
  async ({ programId, updates }, { rejectWithValue }) => {
    try {
      const payload = mapProgramToUpdatePayload(updates);
      const response = await updateEnhancementBundle(programId, payload);
      return mapBundleToProgram(response);
    } catch {
      return rejectWithValue("Failed to save changes");
    }
  },
);

// ============================================================================
// Slice
// ============================================================================

const programEditorSlice = createSlice({
  name: "programEditor",
  initialState,
  reducers: {
    // Workflow actions
    setCreatedProgramId(state, action: PayloadAction<string | null>) {
      state.createdProgramId = action.payload;
    },
    setActiveTab(state, action: PayloadAction<ProgramEditorTab>) {
      state.activeTab = action.payload;
    },
    resetEditor(state) {
      Object.assign(state, initialState);
    },

    // Details actions
    setProgramDetails(state, action: PayloadAction<ProgramDetailsFormValues>) {
      state.programDetails = action.payload;
      state.pendingChanges = true;
    },

    // Content actions
    setComponents(state, action: PayloadAction<ProgramComponents>) {
      state.components = action.payload;
      state.pendingChanges = true;
    },
    addCourses(state, action: PayloadAction<string[]>) {
      const newCourses = action.payload.filter(
        (id) => !state.components.courses.includes(id),
      );
      state.components.courses = [...state.components.courses, ...newCourses];
      state.pendingChanges = true;
    },
    removeCourse(state, action: PayloadAction<string>) {
      state.components.courses = state.components.courses.filter(
        (id) => id !== action.payload,
      );
      state.pendingChanges = true;
    },
    addEvents(state, action: PayloadAction<string[]>) {
      const newEvents = action.payload.filter(
        (id) => !state.components.events.includes(id),
      );
      state.components.events = [...state.components.events, ...newEvents];
      state.pendingChanges = true;
    },
    removeEvent(state, action: PayloadAction<string>) {
      state.components.events = state.components.events.filter(
        (id) => id !== action.payload,
      );
      state.pendingChanges = true;
    },
    addResourceTag(state, action: PayloadAction<string>) {
      if (!state.components.resourceTags.includes(action.payload)) {
        state.components.resourceTags = [
          ...state.components.resourceTags,
          action.payload,
        ];
        state.pendingChanges = true;
      }
    },
    removeResourceTag(state, action: PayloadAction<string>) {
      state.components.resourceTags = state.components.resourceTags.filter(
        (tag) => tag !== action.payload,
      );
      state.pendingChanges = true;
    },

    // Permissions actions
    setSubscriptionAvailability(
      state,
      action: PayloadAction<SubscriptionAvailability>,
    ) {
      state.subscriptionAvailability = action.payload;
      state.pendingChanges = true;
    },
    updateTierAvailability(
      state,
      action: PayloadAction<{ tierId: string; status: ProgramAccessStatus }>,
    ) {
      state.subscriptionAvailability[action.payload.tierId] =
        action.payload.status;
      state.pendingChanges = true;
    },

    // Auto-save actions
    setSaveStatus(state, action: PayloadAction<SaveStatus>) {
      state.saveStatus = action.payload;
    },
    setLastSavedAt(state, action: PayloadAction<string | null>) {
      state.lastSavedAt = action.payload;
    },
    setPendingChanges(state, action: PayloadAction<boolean>) {
      state.pendingChanges = action.payload;
    },

    // UI actions
    setCoursesPickerOpen(state, action: PayloadAction<boolean>) {
      state.isCoursesPickerOpen = action.payload;
    },
    setEventsPickerOpen(state, action: PayloadAction<boolean>) {
      state.isEventsPickerOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Load program for edit
    builder
      .addCase(loadProgramForEdit.pending, (state) => {
        state.loading.loadProgram = true;
        state.error.loadProgram = null;
      })
      .addCase(loadProgramForEdit.fulfilled, (state, action) => {
        state.loading.loadProgram = false;
        state.createdProgramId = action.payload.id;
        state.programDetails = {
          name: action.payload.name,
          description: action.payload.description ?? "",
          tagline: action.payload.tagline ?? "",
          thumbnailUrl: action.payload.thumbnailUrl ?? "",
          price: action.payload.price,
          isPublished: action.payload.isPublished,
        };
        state.components = action.payload.components;
        state.subscriptionAvailability =
          action.payload.subscriptionAvailability;
      })
      .addCase(loadProgramForEdit.rejected, (state, action) => {
        state.loading.loadProgram = false;
        state.error.loadProgram = action.payload ?? "Failed to load program";
      });

    // Create program
    builder
      .addCase(createProgram.pending, (state) => {
        state.loading.createProgram = true;
        state.error.createProgram = null;
      })
      .addCase(createProgram.fulfilled, (state, action) => {
        state.loading.createProgram = false;
        state.createdProgramId = action.payload.id;
        state.programDetails = {
          name: action.payload.name,
          description: action.payload.description ?? "",
          tagline: action.payload.tagline ?? "",
          thumbnailUrl: action.payload.thumbnailUrl ?? "",
          price: action.payload.price,
          isPublished: action.payload.isPublished,
        };
      })
      .addCase(createProgram.rejected, (state, action) => {
        state.loading.createProgram = false;
        state.error.createProgram =
          action.payload ?? "Failed to create program";
      });

    // Update program (auto-save)
    builder
      .addCase(updateProgram.pending, (state) => {
        state.loading.updateProgram = true;
        state.error.updateProgram = null;
        state.saveStatus = "saving";
      })
      .addCase(updateProgram.fulfilled, (state) => {
        state.loading.updateProgram = false;
        state.saveStatus = "saved";
        state.lastSavedAt = new Date().toISOString();
        state.pendingChanges = false;
      })
      .addCase(updateProgram.rejected, (state, action) => {
        state.loading.updateProgram = false;
        state.error.updateProgram = action.payload ?? "Failed to save changes";
        state.saveStatus = "error";
      });
  },
});

// ============================================================================
// Actions Export
// ============================================================================

export const {
  setCreatedProgramId,
  setActiveTab,
  resetEditor,
  setProgramDetails,
  setComponents,
  addCourses,
  removeCourse,
  addEvents,
  removeEvent,
  addResourceTag,
  removeResourceTag,
  setSubscriptionAvailability,
  updateTierAvailability,
  setSaveStatus,
  setLastSavedAt,
  setPendingChanges,
  setCoursesPickerOpen,
  setEventsPickerOpen,
} = programEditorSlice.actions;

// ============================================================================
// Selectors
// ============================================================================

export const selectProgramEditorState = (state: RootState) =>
  state.programEditor;
export const selectCreatedProgramId = (state: RootState) =>
  state.programEditor.createdProgramId;
export const selectActiveTab = (state: RootState) =>
  state.programEditor.activeTab;
export const selectProgramDetails = (state: RootState) =>
  state.programEditor.programDetails;
export const selectComponents = (state: RootState) =>
  state.programEditor.components;
export const selectSubscriptionAvailability = (state: RootState) =>
  state.programEditor.subscriptionAvailability;
export const selectSaveStatus = (state: RootState) =>
  state.programEditor.saveStatus;
export const selectLastSavedAt = (state: RootState) =>
  state.programEditor.lastSavedAt;
export const selectPendingChanges = (state: RootState) =>
  state.programEditor.pendingChanges;
export const selectIsStage2 = (state: RootState) =>
  state.programEditor.createdProgramId !== null;
export const selectLoading = (state: RootState) => state.programEditor.loading;
export const selectError = (state: RootState) => state.programEditor.error;
export const selectIsCoursesPickerOpen = (state: RootState) =>
  state.programEditor.isCoursesPickerOpen;
export const selectIsEventsPickerOpen = (state: RootState) =>
  state.programEditor.isEventsPickerOpen;

// ============================================================================
// Reducer Export
// ============================================================================

export const programEditorReducer = programEditorSlice.reducer;
export default programEditorSlice.reducer;
